import { useEffect } from 'react';
import { useRive, Layout, Fit, decodeFont } from '@rive-app/react-canvas';

import { generateClassName } from '@klv/shared/utils/functions';
import { useTranslation } from '@klv/hooks/useTranslation';

import * as styles from './styles/rive.module.css';

import type { FontAsset } from '@rive-app/react-canvas';
import type { IRiveAtomProps } from './types';


export const Rive = ({
  ariaLabelText,
  aspectRatio = '1',
  className = '',
  flexibleLayout,
  height,
  riveFile,
  riveText,
  ...props
}: IRiveAtomProps) => { 
  // if we're on an INTL site, we need to add the language code to the path
  const { t } = useTranslation();
  let pathPrefix = t('langCodes_pathPrefix');
  if (pathPrefix !== '') {
    pathPrefix = `/${pathPrefix}`;
  }
  /*
  * if we're on development, we need to add the domain to the URL. This will result
  * in a CORS error in your browser. You should disable CORS in your browser to
  * see the Rive animation.
  **/
  let riveUrl = riveFile.mediaItemUrl;
  
  /** 
   * if on production, remove the domain from the URL. This will result in a relative
   * path to the Rive animation. If we were to keep the domain name, we would get a
   * CORS error in the browser.
   **/ 
  if (riveFile.mediaItemUrl.startsWith('http')) {
    const uploadsIndex = riveUrl.indexOf('/uploads');

    if (uploadsIndex !== -1) {
      riveUrl = `${pathPrefix}/static/rive${riveUrl.substring(uploadsIndex)}`;
    } else {
      riveUrl = (new URL(riveFile.mediaItemUrl)).pathname;
    }
  }
  
  const { rive, RiveComponent } = useRive({ 
    src: riveUrl,
    autoplay: true,
    layout:new Layout({ 
      fit: flexibleLayout ? Fit.Layout : Fit.Contain,
    }),
    assetLoader: (asset, bytes) => {

      // If the asset has a CDN UUID or bytes, we don't need to load it
      if (asset.cdnUuid.length > 0 || bytes.length > 0) {
        return false;
      }

      // If the asset is a referenced font, we need to load it
      if (asset.isFont) {
        // Fetch the font file by name and extension.
        fetch(`/fonts/${asset.name}.ttf`).then(async (res) => {
          // decodeFont creates a Rive-specific Font object that `setFont()` takes
          // on the asset from assetLoader
          const font = await decodeFont(new Uint8Array(await res.arrayBuffer()));
          (asset as FontAsset).setFont(font);
      
          // Be sure to call unref to release any references.
          // This allows the engine to clean it up when it is not used by any more animations.
          font.unref();
        });
        return true;
      }
      
      return false;
    },
  });
  
  useEffect(() => {
    // This effect runs when the Rive instance is ready
    if (rive && riveText && riveText.length > 0) {
      riveText.forEach(({ key, value, path }) => {
        if (path) {
          rive.setTextRunValueAtPath(key, value, path);
        } else {
          rive.setTextRunValue(key, value);
        }
      });
    }
  }, [rive, riveText]);

  let riveStyle: { aspectRatio?: string; height?: string; } = { aspectRatio: aspectRatio };
  if (flexibleLayout) {
    riveStyle = { height: `${height}px` };
  }

  return (
    <div className={generateClassName([className, styles.rive])} {...props}>
      <RiveComponent style={riveStyle} aria-label={ariaLabelText} role='img' />
    </div>
  );
};
